import { useCallback } from "react";
import useSWR from "swr";
import { auth, vceFirestore } from "../../configs/FirebaseConfig";
import { FIRESTORE_PRODUCT_LINKS_TABLE } from "../../constants/FirestoreConstant";
import {
  DEFAULT_SYNCED_PROPERTIES,
  sanitiseSyncedProperties,
} from "../../constants/ProductSyncConstant";
import { findLinkForProduct, linkCounterpart, linkDocumentId } from "../../functions/productSync";

/**
 * "This VCE product and that HVO product are the same product."
 *
 * There is no shared key between the two product collections - no sku, no ean, no article
 * number - so this statement cannot be computed, only recorded. It lives in its own collection
 * rather than on the pick route: a pick route entry is a *spot in the freezer* and exists for
 * the walking order, while a link is about the product data. Two products can be one product
 * without sharing a spot (bulk vs. retail packaging), and a spot can hold products that are not
 * the same product. Folding the two together would make each of them lie about the other.
 *
 * Stored in the VCE Firestore for the same reason the pick route is: one shared administration,
 * in the project this CMS authenticates against, readable even when the HVO session is missing.
 */
const linksCollection = () => vceFirestore.collection(FIRESTORE_PRODUCT_LINKS_TABLE);

const SWR_KEY = "productLinks";

/** Firestore refuses a batch over 500 writes, and the first linking round is the whole catalogue. */
const BATCH_LIMIT = 450;

// Stable empty array: `links || []` would hand out a new reference every render and re-run
// every memo on the products page while the collection is still loading.
const NO_LINKS = [];

const currentUserEmail = () => (auth.currentUser && auth.currentUser.email) || null;

/**
 * The document reference a link between these two products must be written to.
 *
 * Derived from the VCE product id (see `linkDocumentId`) so that two people doing this at the
 * same time write the *same* document instead of two: the one-to-one guards below all read a
 * list that was fetched a moment ago, and a fresh auto-id collides with nothing, so a second
 * tab would happily create a full second set of links. Last writer wins is not perfect, but
 * "one link per VCE product" then holds in Firestore and not only in the tab that got there
 * first.
 *
 * A product id that cannot be a document id falls back to an auto-id - no worse than before,
 * and never a write to a path this code did not mean.
 */
const linkReferenceFor = (vceProductId) => {
  const documentId = linkDocumentId(vceProductId);
  return !documentId ? linksCollection().doc() : linksCollection().doc(documentId);
};

const fetchProductLinks = async () => {
  const snapshot = await linksCollection().get();
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      // Sanitised on read: a property that was removed from the registry since this link was
      // written must not survive in memory and get re-saved on the next edit.
      syncedProperties: sanitiseSyncedProperties(data.syncedProperties),
    };
  });
};

/**
 * Read and maintain the VCE <-> HVO product links, and the per-link list of properties that may
 * be copied across. Reads through SWR like the other collections; writes go to Firestore and
 * then revalidate.
 */
export const useProductLinks = () => {
  const { data: links, error, isLoading, mutate } = useSWR(SWR_KEY, fetchProductLinks, {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    dedupingInterval: 10000,
  });

  /** The link this product belongs to, from either shop's side. */
  const getLinkForProduct = useCallback(
    (shopSource, productId) => findLinkForProduct(links || NO_LINKS, shopSource, productId),
    [links],
  );

  /** The product in the other shop that this product is linked to, or null. */
  const getCounterpart = useCallback(
    (shopSource, productId) => {
      const link = findLinkForProduct(links || NO_LINKS, shopSource, productId);
      return !link ? null : linkCounterpart(link, shopSource);
    },
    [links],
  );

  const isLinked = useCallback(
    (shopSource, productId) => !!findLinkForProduct(links || NO_LINKS, shopSource, productId),
    [links],
  );

  /**
   * Record that a VCE product and an HVO product are the same product.
   *
   * A link is one-to-one on purpose: if one product could be linked twice, a single edit would
   * fan out to several products and there would be no answer to "which one is the same product".
   * Both sides are therefore checked first, and the existing link is reported back instead of a
   * second one being created.
   *
   * `syncedProperties` is seeded from the registry defaults - the physical facts on, price off -
   * and can be changed per link afterwards.
   *
   * That one-to-one check is only as good as the list it reads. While the collection has not
   * loaded - or its read failed, which is what a missing Firestore rule on this new collection
   * looks like - the list is empty rather than known to be empty, and every check would pass:
   * a product that is already linked would get a second link, and `findLinkForProduct` would
   * then pick whichever of the two an unordered read returns first. So an unknown list refuses
   * the write instead of guessing.
   */
  const createLink = useCallback(
    async ({ vceProduct, hvoProduct, syncedProperties }) => {
      if (!vceProduct || !vceProduct.id || !hvoProduct || !hvoProduct.id) {
        throw new Error("productLinks/incomplete");
      }
      if (!links) {
        return { ok: false, reason: "links-unavailable" };
      }

      const existing = links;
      const vceLink = findLinkForProduct(existing, "VCE", vceProduct.id);
      if (!!vceLink) {
        return { ok: false, reason: "already-linked", shopSource: "VCE", link: vceLink };
      }
      const hvoLink = findLinkForProduct(existing, "HVO", hvoProduct.id);
      if (!!hvoLink) {
        return { ok: false, reason: "already-linked", shopSource: "HVO", link: hvoLink };
      }

      const now = Date.now();
      const email = currentUserEmail();
      const document = {
        vceProductId: vceProduct.id,
        hvoProductId: hvoProduct.id,
        // Names are stored as a readable label for the CMS lists, never as the key: a product
        // renamed in one shop must not silently break the link.
        vceName: vceProduct.name || "",
        hvoName: hvoProduct.name || "",
        syncedProperties:
          syncedProperties === undefined
            ? [...DEFAULT_SYNCED_PROPERTIES]
            : sanitiseSyncedProperties(syncedProperties),
        linkedBy: email,
        linkedAt: now,
        updatedAt: now,
        updatedBy: email,
      };

      // Same deterministic id as the bulk round, for the same reason: two people linking the
      // same pair by hand should end up with one document, not two.
      const reference = linkReferenceFor(vceProduct.id);
      await reference.set(document);
      await mutate();
      return { ok: true, link: { id: reference.id, ...document } };
    },
    [links, mutate],
  );

  /**
   * Record a whole round of links at once - the first fill, after the proposal has been ticked.
   *
   * Doing this one `add()` at a time would be a few hundred round trips and a few hundred SWR
   * revalidations, so it goes out in batches of 450 (Firestore refuses over 500) and
   * revalidates once at the end.
   *
   * The one-to-one rule is checked twice here, against two different things:
   *  - the links already stored, the same check `createLink` does;
   *  - the entries earlier in *this* batch. That second one is the whole reason this cannot
   *    just loop over `createLink`: its guard reads the stored list, which does not yet contain
   *    what this round is about to add. Two proposals pointing at one counterpart would both
   *    pass it, and a batch commits them both - leaving a product linked twice, with
   *    `findLinkForProduct` returning whichever an unordered read hands over first.
   *
   * The proposal already refuses to tick a conflict, but this is the layer that must be right:
   * entries can also arrive from a retry or from a stale screen.
   *
   * Reports per entry rather than a total. "310 aangemaakt, 4 overgeslagen" is not usable if
   * nobody can see *which* four, and those four are exactly the ones needing a human.
   *
   * @param {Object[]} entries - `{ key?, vceProduct, hvoProduct, syncedProperties? }`
   * @param {Function} [onProgress] - called with `{ committed, total }` after each batch
   */
  const createLinks = useCallback(
    async (entries, { onProgress } = {}) => {
      const list = (entries || []).filter((entry) => !!entry);

      const describe = (entry, index) => ({
        index,
        key: entry.key || null,
        vceProductId: (entry.vceProduct && entry.vceProduct.id) || null,
        hvoProductId: (entry.hvoProduct && entry.hvoProduct.id) || null,
        vceName: (entry.vceProduct && entry.vceProduct.name) || "",
        hvoName: (entry.hvoProduct && entry.hvoProduct.name) || "",
      });

      // Same refusal as createLink, for the same reason: an unknown list of links is not an
      // empty one, and every one-to-one check below would pass against it.
      if (!links) {
        return {
          ok: false,
          reason: "links-unavailable",
          created: 0,
          skipped: list.length,
          results: list.map((entry, index) => ({
            ...describe(entry, index),
            ok: false,
            reason: "links-unavailable",
          })),
        };
      }

      const storedVce = new Set(links.map((existing) => existing.vceProductId).filter((id) => !!id));
      const storedHvo = new Set(links.map((existing) => existing.hvoProductId).filter((id) => !!id));
      const batchVce = new Set();
      const batchHvo = new Set();

      const now = Date.now();
      const email = currentUserEmail();

      const results = [];
      const writes = [];

      list.forEach((entry, index) => {
        const base = describe(entry, index);
        const { vceProduct, hvoProduct } = entry;

        if (!base.vceProductId || !base.hvoProductId) {
          results.push({ ...base, ok: false, reason: "incomplete" });
          return;
        }
        if (storedVce.has(base.vceProductId)) {
          results.push({ ...base, ok: false, reason: "already-linked", shopSource: "VCE" });
          return;
        }
        if (storedHvo.has(base.hvoProductId)) {
          results.push({ ...base, ok: false, reason: "already-linked", shopSource: "HVO" });
          return;
        }
        // Reported apart from "already-linked": this one is not about the database, it is two
        // rows of the same proposal wanting the same product, and the fix is a human choice.
        if (batchVce.has(base.vceProductId)) {
          results.push({ ...base, ok: false, reason: "duplicate-in-batch", shopSource: "VCE" });
          return;
        }
        if (batchHvo.has(base.hvoProductId)) {
          results.push({ ...base, ok: false, reason: "duplicate-in-batch", shopSource: "HVO" });
          return;
        }

        batchVce.add(base.vceProductId);
        batchHvo.add(base.hvoProductId);

        const document = {
          vceProductId: base.vceProductId,
          hvoProductId: base.hvoProductId,
          vceName: vceProduct.name || "",
          hvoName: hvoProduct.name || "",
          syncedProperties:
            entry.syncedProperties === undefined
              ? [...DEFAULT_SYNCED_PROPERTIES]
              : sanitiseSyncedProperties(entry.syncedProperties),
          linkedBy: email,
          linkedAt: now,
          updatedAt: now,
          updatedBy: email,
        };

        const reference = linkReferenceFor(base.vceProductId);
        writes.push({ reference, document });
        results.push({ ...base, ok: true, reason: null, id: reference.id, link: { id: reference.id, ...document } });
      });

      // A failing batch does not undo the batches before it, so the loop stops rather than
      // throwing: what already went in is real, and the caller has to be able to say which of
      // the 300 links exist and which still have to be made. Throwing here would discard
      // exactly that, and the retry would then be over the whole list again.
      let writeError = null;
      let committed = 0;

      for (let start = 0; start < writes.length; start += BATCH_LIMIT) {
        const chunk = writes.slice(start, start + BATCH_LIMIT);
        try {
          const batch = vceFirestore.batch();
          chunk.forEach(({ reference, document }) => batch.set(reference, document));
          await batch.commit();
          committed += chunk.length;
          if (!!onProgress) onProgress({ committed, total: writes.length });
        } catch (error) {
          writeError = error;
          break;
        }
      }

      if (!!writeError) {
        const notWritten = new Set(writes.slice(committed).map(({ reference }) => reference.id));
        results.forEach((result) => {
          if (!result.ok || !notWritten.has(result.id)) return;
          result.ok = false;
          result.reason = "write-failed";
          delete result.link;
        });
      }

      // One revalidation for the whole round, not one per document.
      if (committed > 0) await mutate();

      const created = results.filter((result) => result.ok).length;
      return {
        ok: !writeError,
        reason: !writeError ? null : "write-failed",
        error: writeError,
        created,
        skipped: results.length - created,
        results,
      };
    },
    [links, mutate],
  );

  /**
   * Break the link. Only the statement "these are the same product" is removed - values already
   * copied stay where they are, because they are now that product's own data.
   */
  const removeLink = useCallback(
    async (linkId) => {
      if (!linkId) throw new Error("productLinks/no-id");
      await linksCollection().doc(linkId).delete();
      await mutate();
      return { ok: true };
    },
    [mutate],
  );

  /**
   * Change which properties this link copies. Everything is put through the registry first, so
   * a key that no longer exists - or one that is on NEVER_SYNC - cannot be stored here and
   * later acted on.
   */
  const updateSyncedProperties = useCallback(
    async (linkId, properties) => {
      if (!linkId) throw new Error("productLinks/no-id");
      const cleaned = sanitiseSyncedProperties(properties);
      await linksCollection()
        .doc(linkId)
        .update({ syncedProperties: cleaned, updatedAt: Date.now(), updatedBy: currentUserEmail() });
      await mutate();
      return { ok: true, syncedProperties: cleaned };
    },
    [mutate],
  );

  /** Keep the stored display names in step after a rename in either shop. */
  const refreshLinkNames = useCallback(
    async (linkId, { vceName, hvoName }) => {
      if (!linkId) throw new Error("productLinks/no-id");
      const updates = { updatedAt: Date.now(), updatedBy: currentUserEmail() };
      if (vceName !== undefined) updates.vceName = vceName || "";
      if (hvoName !== undefined) updates.hvoName = hvoName || "";
      await linksCollection().doc(linkId).update(updates);
      await mutate();
    },
    [mutate],
  );

  /** The properties this link syncs; empty for a product that is not linked to anything. */
  const syncedPropertiesFor = useCallback(
    (shopSource, productId) => {
      const link = findLinkForProduct(links || NO_LINKS, shopSource, productId);
      return !link ? [] : sanitiseSyncedProperties(link.syncedProperties);
    },
    [links],
  );

  return {
    links: links || NO_LINKS,
    isLoading,
    // Whether the collection has ever been read successfully. `links` is handed out as an empty
    // array while it has not, and "no links yet" and "we do not know the links" look identical
    // from the outside - which matters, because one of them may not be linked against.
    isLoaded: !!links,
    error,
    refresh: mutate,
    getLinkForProduct,
    getCounterpart,
    isLinked,
    syncedPropertiesFor,
    createLink,
    createLinks,
    removeLink,
    updateSyncedProperties,
    refreshLinkNames,
  };
};
